/**
 * ============================================================================
 * useKbPageLinkResolver — Resolve em batch metadados dos artigos-destino
 *                        referenciados por marks KbPageLink. Cache por page_id
 *                        via TanStack Query. Uma única chamada por conjunto.
 *
 * Regra de segurança: se `has_access = false`, título é NULL — resolver não
 * vaza título de artigo sem permissão.
 *
 * CHANGELOG:
 *   RELEASE: 2026-07-08 v1.0.0 - Versão inicial.
 * ============================================================================
 */
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { logger } from "@/lib/logger";

export type KbLinkState = "ok" | "no_access" | "archived" | "removed" | "draft" | "in_review";

export interface KbResolvedLink {
  page_id: string;
  space_id: string | null;
  space_slug: string | null;
  space_name: string | null;
  collection_name: string | null;
  title: string | null;       // NULL quando sem acesso ou removido
  slug: string | null;
  status: string;
  is_archived: boolean;
  has_access: boolean;
  state: KbLinkState;
}

/**
 * Resolve N pageIds de uma vez. Chave de cache determinística (ordenada) para
 * reaproveitar batches iguais. Retorna Map<pageId, KbResolvedLink> para
 * lookup O(1) na renderização.
 */
export function useKbPageLinkResolver(pageIds: string[] | undefined | null) {
  const unique = Array.from(new Set((pageIds ?? []).filter(Boolean))).sort();
  const enabled = unique.length > 0;

  return useQuery({
    queryKey: ["kb", "page-link-resolver", unique],
    enabled,
    staleTime: 60_000, // 1 min — links não mudam frequentemente
    queryFn: async () => {
      const { data, error } = await supabase.rpc("kb_resolve_page_links", {
        p_page_ids: unique,
        p_tenant_hint: null,
      });
      if (error) {
        logger.error("[useKbPageLinkResolver]", "falha ao resolver", error);
        throw error;
      }
      const map = new Map<string, KbResolvedLink>();
      for (const row of (data ?? []) as KbResolvedLink[]) {
        map.set(row.page_id, row);
      }
      return map;
    },
  });
}
